import { useState, useRef, useEffect } from "react";
import EditorView from "./EditorView";
import ListView from "./ListView";
import { useNavigate } from "react-router";

const uid = () => Math.random().toString(36).slice(2, 10);

const emptyPost = () => ({
    id: uid(),
    title: "",
    shootDate: "",
    images: [], // { id, url, name }
});
const API = "";

const getPosts = () => fetch(`${API}/api/posts`).then(r => r.json())

export default function PhotoPostsAdmin() {
    const [posts, setPosts] = useState([]);
    const [view, setView] = useState("list"); // "list" | "editor"
    const [draft, setDraft] = useState(null);
    const [confirmDeleteId, setConfirmDeleteId] = useState(null);
    const fileInputRef = useRef(null);
    const token = sessionStorage.getItem('token')
    const navigate = useNavigate()

    // если сервер ответил 401 (токен истёк/невалиден) — выкидываем на /login
    const authHeaders = () => ({ Authorization: `Bearer ${token}` });
    const handleAuthError = (res) => {
        if (res.status === 401) {
            sessionStorage.removeItem('token');
            navigate('/login');
            return true;
        }
        return false;
    };

    const openCreate = () => {
        setDraft(emptyPost());
        setView("editor");
    };

    const openEdit = (post) => {
        setDraft({ ...post, images: [...post.images] });
        setView("editor");
    };

    const closeEditor = () => {
        setDraft(null);
        setView("list");
    };

    const savePost = async () => {
        if (!draft.title.trim()) return;

        const fd = new FormData();
        fd.append("title", draft.title);
        fd.append("shootDate", draft.shootDate);
        if (draft.files) Array.from(draft.files).forEach(f => fd.append("images", f));

        const isExisting = posts.some((p) => p.id === draft.id);
        const url = isExisting ? `${API}/api/posts/${draft.id}` : `${API}/api/posts`;
        const method = isExisting ? "PUT" : "POST";

        const res = await fetch(url, { method, headers: authHeaders(), body: fd });
        if (handleAuthError(res)) return;

        const post = await res.json();
        setPosts((prev) => {
            if (isExisting) {
                return prev.map((p) => (p.id === post.id ? post : p));
            }
            return [post, ...prev];
        });
        closeEditor();
    };

    const deletePost = async (id) => {
        const res = await fetch(`${API}/api/posts/${id}`, { method: "DELETE", headers: authHeaders() });
        if (handleAuthError(res)) return;

        if (res.ok) {
            setPosts((prev) => prev.filter((p) => p.id !== id));
            setConfirmDeleteId(null);
        }
    };

    const handleFiles = (files) => {
        const list = Array.from(files).map((file) => ({
            id: uid(),
            url: URL.createObjectURL(file),
            name: file.name,
            isNew: true, // ещё не загружена на сервер — для removeImage
        }));
        setDraft((d) => ({ ...d, images: [...d.images, ...list], files }));
    };

    // если картинка уже сохранена на сервере (не isNew) — удаляем её сразу через API,
    // иначе просто убираем из черновика (грузить на сервер было нечего)
    const removeImage = async (imgId) => {
        const img = draft.images.find((i) => i.id === imgId);
        if (!img) return;

        if (!img.isNew && draft.id) {
            try {
                const res = await fetch(`${API}/api/posts/${draft.id}/images/${imgId}`, {
                    method: "DELETE",
                    headers: authHeaders(),
                });
                if (handleAuthError(res)) return;
                if (!res.ok) return; // не удалось удалить на сервере — не трогаем локальный стейт
            } catch (e) {
                console.error(e);
                return;
            }
        }

        setDraft((d) => ({ ...d, images: d.images.filter((i) => i.id !== imgId) }));
        setPosts((prev) =>
            prev.map((p) =>
                p.id === draft.id ? { ...p, images: p.images.filter((i) => i.id !== imgId) } : p
            )
        );
    };

    const formatDate = (iso) => {
        if (!iso) return "Дата не указана";
        const d = new Date(iso + "T00:00:00");
        return d.toLocaleDateString("ru-RU", { day: "2-digit", month: "long", year: "numeric" });
    };

    useEffect(() => {
        if (token) {
            getPosts().then(res => {
                setPosts(res)
            })
        } else {
            navigate('/login')
        }

    }, [])

    return (
        <div className="ppa-root">
            <div className="ppa-container">
                {view === "list" ? (
                    <ListView
                        posts={posts}
                        onCreate={openCreate}
                        onEdit={openEdit}
                        confirmDeleteId={confirmDeleteId}
                        setConfirmDeleteId={setConfirmDeleteId}
                        onDelete={deletePost}
                        formatDate={formatDate}
                    />
                ) : (
                    <EditorView
                        draft={draft}
                        setDraft={setDraft}
                        onSave={savePost}
                        onCancel={closeEditor}
                        onFiles={handleFiles}
                        onRemoveImage={removeImage}
                        fileInputRef={fileInputRef}
                        isNew={!posts.some((p) => p.id === draft.id)}
                    />
                )}
            </div>
        </div>
    );
}